/* ==========================================================================
   #INK JOURNAL — Article behaviour
   --------------------------------------------------------------------------
   1. Builds the table of contents from the H2/H3 the merchant actually wrote
      in the Shopify rich-text editor (Liquid can't introspect that markup).
   2. Highlights the section currently being read.
   3. Drives the reading-progress bar in browsers without scroll-driven CSS
      animations. Where `animation-timeline: scroll()` is supported, CSS owns
      the bar and this code stays out of the way (no scroll listener at all).
   ========================================================================== */

const SELECTOR = '[data-ink-article]';

const slugify = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60) || 'section';

class InkArticle {
  constructor(root) {
    this.root = root;
    this.prose = root.querySelector('[data-ink-prose]');
    this.toc = root.querySelector('[data-ink-toc]');
    this.tocList = root.querySelector('[data-ink-toc-list]');
    this.tocToggle = root.querySelector('[data-ink-toc-toggle]');
    this.reader = root.querySelector('[data-ink-read]');
    this.progress = root.querySelector('[data-ink-progress]');

    this.links = new Map();

    this.wrapWideBlocks();
    this.buildToc();
    this.bindToc();
    this.watch();
  }

  /* Tables (and any oversized embed) must scroll inside their own box —
     the page body must never scroll sideways. */
  wrapWideBlocks() {
    if (!this.prose) return;

    this.prose.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('ink-prose__scroll')) return;
      const shell = document.createElement('div');
      shell.className = 'ink-prose__scroll';
      shell.setAttribute('role', 'region');
      shell.setAttribute('tabindex', '0');
      shell.setAttribute('aria-label', this.root.dataset.inkTableLabel || 'Table');
      table.parentNode.insertBefore(shell, table);
      shell.appendChild(table);
    });
  }

  buildToc() {
    if (!this.prose || !this.toc || !this.tocList) return;

    const headings = Array.from(this.prose.querySelectorAll('h2, h3'));

    // One or zero headings is a list of one — not worth the rail.
    if (headings.length < 2) {
      this.toc.hidden = true;
      return;
    }

    const used = new Set();
    const fragment = document.createDocumentFragment();

    headings.forEach((heading) => {
      if (!heading.id) {
        let id = slugify(heading.textContent || '');
        let n = 2;
        while (used.has(id) || document.getElementById(id)) {
          id = `${slugify(heading.textContent || '')}-${n}`;
          n += 1;
        }
        heading.id = id;
      }
      used.add(heading.id);

      const level = heading.tagName.toLowerCase();
      const item = document.createElement('li');
      item.className = `ink-toc__item ink-toc__item--${level}`;

      const link = document.createElement('a');
      link.className = 'ink-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      link.setAttribute('aria-current', 'false');

      item.appendChild(link);
      fragment.appendChild(item);
      this.links.set(heading.id, link);
    });

    this.tocList.appendChild(fragment);
    this.toc.hidden = false;
    this.headings = headings;
  }

  bindToc() {
    if (!this.toc || !this.tocToggle || this.toc.hidden) return;

    this.tocToggle.addEventListener('click', () => {
      const open = this.toc.classList.toggle('is-open');
      this.tocToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Jumping to a heading should close the mobile panel behind you.
    this.tocList?.addEventListener('click', (event) => {
      if (!event.target.closest('.ink-toc__link')) return;
      this.toc.classList.remove('is-open');
      this.tocToggle.setAttribute('aria-expanded', 'false');
    });
  }

  /* One rAF-throttled scroll handler drives both the active heading and, where
     CSS scroll timelines are unavailable, the progress bar.

     Deliberately not an IntersectionObserver: an observer only fires when a
     target crosses a threshold, so jumping straight to an anchor (a table of
     contents click, a shared #link, back/forward) can land between headings
     and leave the rail marking the wrong section. */
  watch() {
    const bar =
      this.progress && this.reader && !InkArticle.hasNativeTimelines()
        ? this.progress.querySelector('[data-ink-progress-bar]')
        : null;

    if (!this.headings && !bar) return;

    let ticking = false;
    let activeId = null;

    const update = () => {
      ticking = false;

      if (this.headings) {
        const marker = window.innerHeight * 0.35;
        let current = this.headings[0];
        for (const heading of this.headings) {
          if (heading.getBoundingClientRect().top > marker) break;
          current = heading;
        }
        if (current.id !== activeId) {
          activeId = current.id;
          this.links.forEach((link, key) => {
            link.setAttribute('aria-current', key === activeId ? 'true' : 'false');
          });
        }
      }

      if (bar) {
        const rect = this.reader.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const value = total <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / total));
        bar.style.setProperty('--ink-progress-value', String(value));
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  static hasNativeTimelines() {
    return (
      typeof CSS !== 'undefined' &&
      CSS.supports('animation-timeline', 'view()') &&
      CSS.supports('timeline-scope', '--ink-read')
    );
  }
}

function init(scope = document) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root.inkArticle) return;
    root.inkArticle = new InkArticle(root);
  });
}

init();

document.addEventListener('shopify:section:load', (event) => init(event.target));
